import React, {useEffect, useRef, useState} from "react";
import "../styles/admin_page.css";
import {Orders} from "../../models/Orders";
import AdminLayout from "../components/AdminLayout";
import {BASE_URLS} from "../../config/urls";
import {apiFetch} from "../../utils/api";
import {BoxInput, SupplyType} from "../../types/form";
import {useOrderSubmission} from "../../hooks/useOrderSubmission";
import {areBoxesValid, isPhoneMaskedValid} from "../../utils/validators";
import {ERRORS, PAYMENT} from "../../constants/strings";
import {PriceRequest} from "../../models/PriceRequest";
import {Link, useParams} from "react-router-dom";
import CalculatorDropdown from "../../admin/forms/CalculatorDropdown";
import {RadioGroup} from "../components/fields/RadioGroup";
import {BtnSpinner} from "../../components/fields/BtnSpinner";
import {SupplyDTO} from "../../models/SupplyDTO";
import {PhoneForm} from "../../components/forms/PhoneForm";
import {BoxesForm} from "../../components/forms/BoxesForm";

const AdminChangeOrdersPage: React.FC = () => {
    const {id} = useParams();

    // --- данные с бэка ---
    const [order, setOrder] = useState<Orders | null>(null);
    const [supply, setSupply] = useState<SupplyDTO | null>(null);
    const [companies, setCompanies] = useState<string[]>([]);

    // --- стейты формы ---
    const [tel, setTel] = useState("");
    const [telError, setTelError] = useState("");
    const [inputs, setInputs] = useState<BoxInput[]>([
        {length: 0, width: 0, height: 0, amount: 0},
    ]);
    const [supplyType, setSupplyType] = useState<SupplyType>("Короб");
    const [selectedStoreIndex, setSelectedStoreIndex] = useState(0);
    const [willTaken, setWillTaken] = useState(false);
    const [paymentOnline, setPaymentOnline] = useState(false);
    const [ozonNumber, setOzonNumber] = useState("");
    const [departureDate, setDepartureDate] = useState<Date | null>(null);
    const [acceptanceDate, setAcceptanceDate] = useState<Date | null>(null);
    const [comment, setComment] = useState("");
    const [entityIndex, setEntityIndex] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState(false);
    const [changeable, setChangeable] = useState(false);
    const [status, setStatus] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inputsValid, setInputsValid] = useState(true);
    const [isPaying, setIsPaying] = useState(false);

    // custom errors
    const [commentError, setCommentError] = useState("");
    const [ozonError, setOzonError] = useState("");

    const {submit} = useOrderSubmission();

    // производные значения от supply
    const warehouse = supply ? supply.warehouses?.[selectedStoreIndex] ?? null : null;
    const pallet = supplyType === "Монопаллет" || supplyType === 'Паллета';

    const storeIsOzon = warehouse?.store === "Ozon";

    // refs for smooth scroll to first error
    const phoneBlockRef = useRef<HTMLDivElement>(null);
    const boxesErrorRef = useRef<HTMLDivElement>(null);
    const ozonRef = useRef<HTMLInputElement>(null);
    const commentRef = useRef<HTMLTextAreaElement>(null);
    const agreementInputRef = useRef<HTMLInputElement>(null);
    const companiesBlockRef = useRef<HTMLDivElement>(null);

    // ===== useEffect: грузим заказ по id =====
    useEffect(() => {
        if (!id) return;

        const loadOrder = async () => {
            try {
                const resp = await apiFetch(`${BASE_URLS.api}/orders/${id}`, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                    },
                });

                const data: Orders = await resp.json();

                setOrder(data);
                setSupply(data.supply ?? null);
                setCompanies(data.companies?.map(c => c.companyName + " (" + c.inn + ")") ?? []);

                // телефон
                const p = data.phoneNumber ?? "";
                setTel(p ? `(${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}` : "");

                // коробки
                if (data.boxes?.length) {
                    setInputs(
                        data.boxes.map((b) => ({
                            length: b.length,
                            width: b.width,
                            height: b.height,
                            amount: b.amount,
                        }))
                    );
                }

                // тип поставки
                setSupplyType((data.supplyType as SupplyType) ?? "Короб");

                // забор / оплата / озон
                setWillTaken(data.willTaken);
                setPaymentOnline(data.paymentSite);
                setPaymentStatus(!!data.paymentStatus);
                setChangeable(!!data.changeable);
                setStatus(data.status ?? "");
                setOzonNumber(data.numberOzon ?? "");

                // даты
                setDepartureDate(data.departureDate ?? null);
                setAcceptanceDate(data.acceptanceDate ?? null);

                // комментарий
                setComment(data.comment ?? "");

                // юр. лицо
                if (data.companies?.length && data.entity) {
                    const idx = data.companies.findIndex(c => c.companyName.includes(data.entity!))
                    setEntityIndex(Math.max(0, idx));
                } else {
                    setEntityIndex(0);
                }

                // склад / город отправки
                if (data.supply) {
                    const idxWh = data.supply.warehouses.findIndex(
                        (w: any) => w.store === data.store && w.sendCity === data.sendCity
                    );
                    setSelectedStoreIndex(Math.max(0, idxWh));
                }
            } catch (e) {
                console.error("Ошибка загрузки заказа", e);
            }
        };

        loadOrder();
    }, [id]);

    // пока не загрузили order/supply — показываем простой лоадер
    if (!order || !supply) {
        return (
            <AdminLayout>
                <div className="schedule_form">
                    <div className="modal_window_title">Загрузка заявки...</div>
                </div>
            </AdminLayout>
        );
    }

    // ---------------- handlers ----------------
    const handleSave = async () => {
        if (isSubmitting || isPaying) return;
        setIsSubmitting(true);

        // reset errors
        setTelError("");
        setInputsValid(true);
        setOzonError("");
        setCommentError("");

        // collect first error target
        let firstErrorEl: HTMLElement | null = null;

        if (!companies.length) {
            firstErrorEl = companiesBlockRef.current || null;
        }
        if (!isPhoneMaskedValid(tel)) {
            setTelError(ERRORS.PHONE_INVALID);
            if (!firstErrorEl) firstErrorEl = phoneBlockRef.current || null;
        }
        if (!areBoxesValid(inputs)) {
            setInputsValid(false);
            if (!firstErrorEl) firstErrorEl = boxesErrorRef.current || null;
        }
        if (storeIsOzon && !ozonNumber.trim()) {
            setOzonError("Укажите номер заказа Ozon");
            if (!firstErrorEl) firstErrorEl = ozonRef.current || null;
        }
        if (willTaken && !comment.trim()) {
            setCommentError("Пожалуйста, укажите откуда забрать товар");
            if (!firstErrorEl) firstErrorEl = commentRef.current || null;
        }

        if (firstErrorEl) {
            firstErrorEl.scrollIntoView({behavior: "smooth", block: "center"});
            (firstErrorEl as any).focus?.();
            setIsSubmitting(false);
            setIsPaying(false);
            return;
        }

        if (!warehouse) {
            setCommentError("Не удалось определить склад");
            setIsSubmitting(false);
            return;
        }

        const {volume, amount} = inputs.reduce(
            (acc, i) => ({
                volume: acc.volume + i.length * i.width * i.height * i.amount,
                amount: acc.amount + i.amount,
            }),
            {volume: 0, amount: 0}
        );
        const volumeM3 = volume / 1_000_000;

        const priceReq = new PriceRequest(
            warehouse.store,
            warehouse.sendCity,
            volumeM3,
            willTaken,
            pallet,
            amount
        );

        try {
            await submit({
                priceReq,
                boxesInputs: inputs,
                pallet,
                supplyMeta: {
                    entity: companies[entityIndex] ?? order.entity ?? "",
                    departureDate: departureDate ?? supply.departureDate,
                    acceptanceDate: acceptanceDate ?? supply.acceptanceDate,
                    phoneMasked: tel,
                    sendCity: warehouse.sendCity,
                    store: warehouse.store,
                    supplyType,
                    willTaken,
                    payment: paymentOnline,
                    comment,
                    ozonNumber,
                    supplyId: supply.id,
                    paymentStatus,
                    changeable,
                    status,
                    baseOrder: order,
                },
            });

            window.location.assign(`${BASE_URLS.app}/admin/orders/${order.id}`);
            return;
        } catch (e: any) {
            console.error(e);
            setCommentError("Ошибка отправки. Попробуйте ещё раз.");
            agreementInputRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    function deleteOrder(order: Orders) {
        apiFetch(`${BASE_URLS.api}/delete/${order.id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',

            }
        }).then(function () {
            window.location.assign(`${BASE_URLS.app}/admin/orders`);
        })
    }

    // ---------------- render ----------------
    return (
        <AdminLayout>
            <div className="schedule_form" style={{paddingTop: "150px", margin: '0 auto', maxWidth: 900}}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSave();
                    }}
                >
                    <div className="modal_window_title">Заполните все необходимые поля</div>

                    <div
                        className="schedule_form_title"
                        style={{marginTop: 20, marginBottom: 5}}
                    >
                        Юридическое лицо
                    </div>

                    <div ref={companiesBlockRef} tabIndex={-1}>
                        {!companies.length ? (
                            <div className="form_error" style={{height: "auto"}}>
                                Вы еще не заполнили данные о своих компаниях. Пожалуйста,
                                заполните данные в{" "}
                                <Link to="/personal_account">личном кабинете</Link>
                            </div>
                        ) : (
                            <CalculatorDropdown
                                items={companies}
                                handleSelectClick={setEntityIndex}
                                selectTitle={companies[entityIndex]}
                            />
                        )}
                    </div>

                    {/* Phone with error anchor */}
                    <div ref={phoneBlockRef}>
                        <PhoneForm
                            defaultValue={tel}
                            error={telError}
                            onChange={(v) => {
                                setTel(v);
                                setTelError("");
                            }}
                        />
                    </div>

                    <div className="schedule_form_title">
                        Размеры и количество коробок/паллет
                    </div>
                    {!inputsValid && <div
                      ref={boxesErrorRef}
                      className="form_error"
                      style={{
                          visibility: inputsValid ? "hidden" : "visible",
                          top: 155,
                      }}
                    >
                      Коробки заполнены не корректно
                    </div>}
                    <BoxesForm
                        inputs={inputs}
                        onChange={(vals) => {
                            setInputs(vals);
                            setInputsValid(true);
                        }}
                    />

                    <div className="schedule_form_title">Тип поставки</div>
                    <RadioGroup
                        name="supplyType"
                        value={supplyType}
                        options={[
                            {label: "Короб", value: "Короб"},
                            {label: "Монопаллет", value: "Монопаллет"},
                            {label: "Паллета", value: "Паллета"},
                        ]}
                        onChange={(v) => setSupplyType(v as SupplyType)}
                    />

                    <div className="schedule_form_title">Склад</div>
                    <RadioGroup
                        name="store"
                        value={String(selectedStoreIndex)}
                        options={supply.warehouses.map((w: any, i: number) => ({
                            label: w.warehouseName,
                            value: String(i),
                        }))}
                        onChange={(v) => setSelectedStoreIndex(parseInt(v, 10))}
                    />

                    {/* Ozon order number (custom error) */}
                    {storeIsOzon && (
                        <div>
                            <div className="schedule_form_title">Номер заказа (Ozon)</div>
                            <input
                                ref={ozonRef}
                                type="text"
                                onInput={(e: any) => {
                                    setOzonNumber(e.target.value);
                                    if (ozonError) setOzonError("");
                                }}
                                style={{
                                    marginTop: 15,
                                    marginLeft: 13,
                                    height: 25,
                                    width: 300,
                                }}
                                defaultValue={ozonNumber}
                                aria-invalid={!!ozonError}
                            />
                            {ozonError && (
                                <div
                                    className="form_error"
                                    style={{marginLeft: 13, marginTop: 6}}
                                >
                                    {ozonError}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="schedule_form_title">Забрать со склада</div>
                    <RadioGroup
                        name="take"
                        value={willTaken ? "Да" : "Нет"}
                        options={[
                            {label: "Да", value: "Да"},
                            {label: "Нет", value: "Нет"},
                        ]}
                        onChange={(v) => setWillTaken(v === "Да")}
                    />

                    <div className="schedule_form_title">Способ оплаты</div>
                    <RadioGroup
                        name="payment"
                        value={paymentOnline ? PAYMENT.ONLINE : PAYMENT.OFFICE}
                        options={[
                            {label: PAYMENT.ONLINE, value: PAYMENT.ONLINE},
                            {label: PAYMENT.OFFICE, value: PAYMENT.OFFICE},
                        ]}
                        onChange={(v) =>
                            setPaymentOnline(v === PAYMENT.ONLINE)
                        }
                    />

                    <div className="schedule_form_title">Доп. комментарий</div>
                    <textarea
                        ref={commentRef}
                        className="schedule_comment"
                        placeholder="Укажите, откуда забрать товар или напишите иные комментарии"
                        onInput={(e: any) => {
                            setComment(e.target.value);
                            if (commentError) setCommentError("");
                        }}
                        defaultValue={comment}
                        aria-invalid={!!commentError}
                    />
                    {commentError && (
                        <div className="form_error" style={{marginTop: 6}}>
                            {commentError}
                        </div>
                    )}

                    <hr style={{marginTop: 30}}/>
                    <div className="schedule_form_title">Оплачен</div>
                    <RadioGroup
                        name="paymentStatus"
                        value={paymentStatus ? "Да" : "Нет"}
                        options={[
                            {label: "Да", value: "Да"},
                            {label: "Нет", value: "Нет"},
                        ]}
                        onChange={(v) => setPaymentStatus(v === "Да")}
                    />

                    <div className="schedule_form_title">Изменяемый</div>
                    <RadioGroup
                        name="changeable"
                        value={changeable ? "Да" : "Нет"}
                        options={[
                            {label: "Да", value: "Да"},
                            {label: "Нет", value: "Нет"},
                        ]}
                        onChange={(v) => setChangeable(v === "Да")}
                    />

                    <div className="schedule_form_title">Статус</div>
                    <RadioGroup
                        name="status"
                        value={status}
                        options={[
                            {label: "Ожидает на ФФ", value: "Ожидает на ФФ"},
                            {label: "Ожидает оплаты", value: "Ожидает оплаты"},
                            {label: "Доставка на склад", value: "Доставка на склад"},
                            {label: "Доставлен", value: "Доставлен"},
                        ]}
                        onChange={(v) => setStatus(v)}
                    />

                    <button
                        type="submit"
                        className="schedule_form_button"
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                        style={{
                            margin: "30px auto 10px"
                        }}
                    >
                        {isSubmitting ? <BtnSpinner/> : "Сохранить заявку"}
                    </button>
                    <button
                        type="button"
                        className="schedule_form_button"
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                        style={{
                            margin: "10px auto 50px",
                        }}
                        onClick={() => deleteOrder(order)}
                    >
                        {isSubmitting ? <BtnSpinner/> : "Удалить заявку"}
                    </button>
                </form>
            </div>
        </AdminLayout>
    );
};

export default AdminChangeOrdersPage;
